import { useState, useEffect } from "react";
import { governanceApi } from "@/services/api";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import { Bot, Cpu, AlertTriangle, Info, Plus, X, Loader2, MessageSquare } from "lucide-react";

const AUTONOMY_LEVELS = [
  { value: "A0_AUTOMATION", label: "A0 — Full Automation", desc: "No human involvement required", color: "text-[#2E844A]" },
  { value: "A1_AI_ASSIST", label: "A1 — AI Assist", desc: "AI suggests, human decides", color: "text-[#0176D3]" },
  { value: "A2_GOVERNED_AUTONOMOUS", label: "A2 — Governed Autonomous", desc: "AI acts within defined rules", color: "text-[#8C4B00]" },
  { value: "A3_HUMAN_APPROVAL_REQUIRED", label: "A3 — Human Approval Required", desc: "Human must approve every action", color: "text-rose-400" },
];

export default function AgentGovernanceSetup() {
  const [tab, setTab] = useState("skills");
  const [skills, setSkills] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [escalations, setEscalations] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(null);

  const load = () => {
    Promise.all([governanceApi.listSkills(), governanceApi.listPolicies(), governanceApi.listEscalationPolicies(), governanceApi.listChannels()])
      .then(([sk, po, es, ch]) => { setSkills(sk.data); setPolicies(po.data); setEscalations(es.data); setChannels(ch.data); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const TABS = [
    { id: "skills", label: "Agent Skills", count: skills.length },
    { id: "policies", label: "Autonomy Policies", count: policies.length },
    { id: "escalation", label: "Escalation Policies", count: escalations.length },
    { id: "channels", label: "Comm Channels", count: channels.length },
  ];

  if (loading) return <div className="p-6 animate-pulse space-y-4"><div className="h-8 bg-[#F3F3F3] rounded-lg w-64"/><div className="h-40 bg-[#F3F3F3] rounded-xl"/></div>;

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div>
        <div className="flex items-center gap-3 mb-1">
          <h1 className="text-2xl font-bold text-[#181818] font-sans">Agent Governance Foundation</h1>
          <span className="flex items-center gap-1 px-2 py-0.5 rounded text-xs font-mono bg-amber-500/10 text-[#8C4B00] border border-amber-500/30">
            <Info className="w-3 h-3" /> Stage 00 — Config Only
          </span>
        </div>
        <p className="text-sm text-[#706E6B]">Agent execution is NOT enabled in Stage 00. This configures governance policies for future stages.</p>
      </div>

      {/* Autonomy Level Reference */}
      <div className="bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl p-5">
        <p className="text-xs font-mono text-[#706E6B] uppercase tracking-widest mb-3">Canonical Autonomy Levels</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {AUTONOMY_LEVELS.map(al => (
            <div key={al.value} className="flex items-start gap-3 p-3 rounded-lg bg-[#F3F3F3] border border-[#E5E5E5]">
              <Cpu className={`w-4 h-4 mt-0.5 flex-shrink-0 ${al.color}`} />
              <div>
                <p className={`text-xs font-bold font-mono ${al.color}`}>{al.label}</p>
                <p className="text-xs text-[#706E6B] mt-0.5">{al.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex gap-1 bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl p-1 flex-wrap">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              tab === t.id ? "bg-[#EEF4FF] text-[#0176D3] border border-[#0176D3]/30" : "text-[#706E6B] hover:text-[#181818]"
            }`}>
            {t.label}
            <span className={`text-xs px-1.5 py-0.5 rounded font-mono ${tab === t.id ? "bg-[#EEF4FF] text-[#0176D3]" : "bg-[#E5E5E5] text-[#706E6B]"}`}>{t.count}</span>
          </button>
        ))}
      </div>

      {tab === "skills" && (
        <Section title="Agent Skill Definitions" onAdd={() => setShowForm("skill")}>
          {skills.map(sk => (
            <div key={sk.id} className="flex items-center justify-between p-4 bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl hover:border-[#0176D3]/30 transition-colors">
              <div className="flex items-center gap-3">
                <Bot className="w-5 h-5 text-[#0176D3]" />
                <div>
                  <p className="text-sm font-semibold text-[#181818]">{sk.name}</p>
                  <p className="text-xs font-mono text-[#706E6B] mt-0.5">{sk.code}</p>
                  {sk.description && <p className="text-xs text-[#706E6B] mt-0.5">{sk.description}</p>}
                </div>
              </div>
              <StatusBadge status={sk.status} />
            </div>
          ))}
          {skills.length === 0 && <Empty msg="No agent skills defined" />}
        </Section>
      )}

      {tab === "policies" && (
        <Section title="Agent Policies" onAdd={() => setShowForm("policy")}>
          {policies.map(po => {
            const al = AUTONOMY_LEVELS.find(a => a.value === po.autonomy_level);
            return (
              <div key={po.id} className="p-4 bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl hover:border-[#0176D3]/30 transition-colors">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="text-sm font-semibold text-[#181818]">{po.name}</p>
                    <p className="text-xs font-mono text-[#706E6B] mt-0.5">{po.code}</p>
                    {po.skill_name && <p className="text-xs text-[#706E6B] mt-0.5">Skill: {po.skill_name}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    {po.is_default && <span className="text-[10px] font-mono text-[#8C4B00] bg-amber-500/10 border border-amber-500/30 px-1.5 py-0.5 rounded">Default</span>}
                    <StatusBadge status={po.status} />
                  </div>
                </div>
                {al && <span className={`text-xs font-mono ${al.color}`}>{al.label}</span>}
                {po.description && <p className="text-xs text-[#706E6B] mt-1">{po.description}</p>}
              </div>
            );
          })}
          {policies.length === 0 && <Empty msg="No agent policies configured" />}
        </Section>
      )}

      {tab === "escalation" && (
        <Section title="Escalation Policies" onAdd={() => setShowForm("esc")}>
          {escalations.map(es => (
            <div key={es.id} className="p-4 bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-[#181818]">{es.name}</p>
                <StatusBadge status={es.status} />
              </div>
              <p className="text-xs font-mono text-[#706E6B]">{es.code}</p>
              <p className="text-xs text-[#706E6B] mt-1">Escalate to: {es.escalate_to_role}</p>
              {es.trigger_conditions?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {es.trigger_conditions.map(tc => (
                    <span key={tc} className="text-[10px] font-mono text-rose-400 bg-rose-500/10 border border-rose-500/20 px-1.5 py-0.5 rounded">{tc}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
          {escalations.length === 0 && <Empty msg="No escalation policies configured" />}
        </Section>
      )}

      {tab === "channels" && (
        <Section title="Communication Channels" onAdd={() => setShowForm("chan")}>
          {channels.map(ch => (
            <div key={ch.id} className="flex items-center justify-between p-4 bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl">
              <div className="flex items-center gap-3">
                <MessageSquare className="w-5 h-5 text-[#0176D3]" />
                <div>
                  <p className="text-sm font-semibold text-[#181818]">{ch.name}</p>
                  <p className="text-xs font-mono text-[#706E6B] mt-0.5">{ch.channel_type} · {ch.code}</p>
                </div>
              </div>
              <StatusBadge status={ch.status} />
            </div>
          ))}
          {channels.length === 0 && <Empty msg="No communication channels configured" />}
        </Section>
      )}

      {showForm === "skill" && (
        <Modal title="Add Agent Skill" onClose={() => setShowForm(null)}>
          <QuickForm fields={[["code","Skill Code","SKILL-INQUIRY",true],["name","Skill Name","Inquiry & Qualification",true],["description","Description",""]]}
            onSubmit={async d => { await governanceApi.createSkill(d); toast.success("Skill created"); setShowForm(null); load(); }} />
        </Modal>
      )}
      {showForm === "policy" && (
        <Modal title="Add Agent Policy" onClose={() => setShowForm(null)}>
          <PolicyForm skills={skills} onSuccess={() => { setShowForm(null); load(); }} />
        </Modal>
      )}
      {showForm === "esc" && (
        <Modal title="Add Escalation Policy" onClose={() => setShowForm(null)}>
          <EscalationForm onSuccess={() => { setShowForm(null); load(); }} />
        </Modal>
      )}
      {showForm === "chan" && (
        <Modal title="Add Communication Channel" onClose={() => setShowForm(null)}>
          <QuickForm fields={[["code","Code","CHAN-EMAIL",true],["name","Name","Primary Email",true]]}
            onSubmit={async d => { await governanceApi.createChannel({...d, channel_type: "EMAIL"}); toast.success("Channel created"); setShowForm(null); load(); }} />
        </Modal>
      )}
    </div>
  );
}

function PolicyForm({ skills, onSuccess }) {
  const [form, setForm] = useState({ code: "", name: "", skill_id: "", autonomy_level: "A1_AI_ASSIST", is_default: false, description: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await governanceApi.createPolicy(form); toast.success("Policy created"); onSuccess(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3">
      <FormField label="Code" value={form.code} onChange={v=>setForm({...form,code:v})} placeholder="POLICY-INQUIRY" required />
      <FormField label="Name" value={form.name} onChange={v=>setForm({...form,name:v})} placeholder="Default Inquiry Policy" required />
      <SelectField label="Agent Skill" value={form.skill_id} onChange={v=>setForm({...form,skill_id:v})}
        options={[["","Select skill"],...skills.map(s=>[s.id,s.name])]} />
      <SelectField label="Autonomy Level" value={form.autonomy_level} onChange={v=>setForm({...form,autonomy_level:v})}
        options={AUTONOMY_LEVELS.map(al=>[al.value, al.label])} />
      <div className="flex items-center gap-3 p-3 rounded-lg bg-[#F3F3F3] border border-[#E5E5E5]">
        <input type="checkbox" id="isDefault" checked={form.is_default} onChange={e=>setForm({...form,is_default:e.target.checked})} className="w-4 h-4 accent-[#0176D3]" />
        <label htmlFor="isDefault" className="text-sm text-[#444444]">Set as default policy</label>
      </div>
      <SubmitBtn loading={loading} label="Create Policy" />
    </form>
  );
}

function EscalationForm({ onSuccess }) {
  const [form, setForm] = useState({ code: "", name: "", escalate_to_role: "EVENT_MANAGER", description: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await governanceApi.createEscalationPolicy(form); toast.success("Escalation Policy created"); onSuccess(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3">
      <FormField label="Code" value={form.code} onChange={v=>setForm({...form,code:v})} placeholder="ESC-DEFAULT" required />
      <FormField label="Name" value={form.name} onChange={v=>setForm({...form,name:v})} placeholder="Default Escalation" required />
      <SelectField label="Escalate To Role" value={form.escalate_to_role} onChange={v=>setForm({...form,escalate_to_role:v})}
        options={[["EVENT_MANAGER","Event Manager"],["TENANT_ADMIN","Tenant Admin"],["OPERATIONS_USER","Operations User"]]} />
      <SubmitBtn loading={loading} label="Create Escalation Policy" />
    </form>
  );
}

function Section({ title, onAdd, children }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-[#444444] font-sans">{title}</h2>
        <button onClick={onAdd} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#EEF4FF] border border-[#0176D3]/30 text-[#0176D3] text-xs font-medium hover:bg-[#E5E5E5]">
          <Plus className="w-3.5 h-3.5" /> Add
        </button>
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
function Empty({ msg }) {
  return <div className="text-center py-8 text-sm text-[#706E6B] bg-[#F3F3F3] border border-dashed border-[#E5E5E5] rounded-xl">{msg}</div>;
}
function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md bg-[#F3F3F3] border border-[#E5E5E5] rounded-2xl shadow-2xl p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-base font-semibold text-[#181818] font-sans">{title}</h3>
          <button onClick={onClose} className="text-[#706E6B] hover:text-[#181818]"><X className="w-4 h-4" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
function QuickForm({ fields, onSubmit }) {
  const [form, setForm] = useState(Object.fromEntries(fields.map(([k]) => [k, ""])));
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await onSubmit(form); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); setLoading(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3">
      {fields.map(([k,l,p,req]) => <FormField key={k} label={l} value={form[k]} onChange={v=>setForm({...form,[k]:v})} placeholder={p} required={req} />)}
      <SubmitBtn loading={loading} label="Create" />
    </form>
  );
}
function FormField({ label, value, onChange, placeholder, required }) {
  return (
    <div>
      <label className="block text-xs font-mono text-[#706E6B] uppercase tracking-widest mb-1">{label}{required && " *"}</label>
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} required={required}
        className="w-full px-3 py-2 rounded-lg bg-[#F3F3F3] border border-[#E5E5E5] text-[#181818] text-sm placeholder:text-[#706E6B] focus:outline-none focus:border-[#0176D3] transition-colors" />
    </div>
  );
}
function SelectField({ label, value, onChange, options }) {
  return (
    <div>
      <label className="block text-xs font-mono text-[#706E6B] uppercase tracking-widest mb-1">{label}</label>
      <select value={value} onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg bg-[#F3F3F3] border border-[#E5E5E5] text-[#181818] text-sm focus:outline-none focus:border-[#0176D3] transition-colors">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}
function SubmitBtn({ loading, label }) {
  return (
    <button type="submit" disabled={loading}
      className="w-full py-2.5 rounded-lg bg-[#0176D3] text-white text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 mt-2">
      {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : label}
    </button>
  );
}
